import { Action } from '../actions';
import {
    ADD_TAGS_IN_TAG_CLOUD_TARGETING,
    CHANGE_TAG_IN_TAG_CLOUD_TARGETING,
    CLOSE_TAG_CLOUD_TARGETING,
    DELETE_TAG_IN_TAG_CLOUD_TARGETING,
    LOAD_TAG_CLOUD_TARGETING,
    OPEN_TAG_CLOUD_TARGETING,
    SAVE_TAG_CLOUD_TARGETING,
    SOFT_DELETE_TAG_IN_TAG_CLOUD_TARGETING,
    TagCloudTargetingCloseAction,
    TagCloudTargetingLoadAction,
    TagCloudTargetingOpenAction,
    TagCloudTargetingSaveAction,
    TagCloudTargetingsUpdateAction,
    TagInTagCloudTargetingChangeAction,
    TagInTagCloudTargetingDeleteAction,
    TagInTagCloudTargetingSoftDeleteAction,
    TagsInTagCloudTargetingAddAction,
    UPDATE_TAG_CLOUD_TARGETINGS
} from '../actions/tagCloudTargeting';
import { StringHelpers } from '../service/StringHelpers/StringHelpers';
import {
    addTags,
    extendTagValue,
    mapTag,
    mapTagValueToString
} from '../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { State } from '../typings/state';
import { TagCloudTargetingsState, TagState } from '../typings/tagCloudTargeting';

const openTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagCloudTargetingOpenAction
): TagCloudTargetingsState => {
    return {
        ...state,
        [action.editorId]: {
            ...state[action.editorId],
            isEditorOpen: true
        }
    };
};

const closeTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagCloudTargetingCloseAction
): TagCloudTargetingsState => {
    const { editorId } = action;
    const savedValue = state[editorId].savedValue || {
        tags: []
    };

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            ...savedValue,
            isEditorOpen: false
        }
    };
};

const saveTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagCloudTargetingSaveAction
): TagCloudTargetingsState => {
    const { editorId, onTargetingSave } = action;

    const tags = onTargetingSave ? onTargetingSave(state[editorId].tags) : state[editorId].tags;
    const savedValue = {
        tags
    };

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            ...savedValue,
            isEditorOpen: false,
            savedValue
        }
    };
};

const addTagsInTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagsInTagCloudTargetingAddAction
): TagCloudTargetingsState => {
    const { editorId, tags, getInternalValueFromUserInput, canHaveBothTargetingModes } = action;

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            tags: addTags(state[editorId].tags, tags, getInternalValueFromUserInput, canHaveBothTargetingModes)
        }
    };
};

const changeTagInTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagInTagCloudTargetingChangeAction
): TagCloudTargetingsState => {
    const { editorId, tag, newValue, getInternalValueFromUserInput } = action;
    const oldValue = mapTagValueToString(tag.value);
    const existingTags = state[editorId].tags;
    const newValueAlreadyExists = existingTags.some(existingTag => {
        return StringHelpers.equalSubstring(mapTagValueToString(existingTag.value), mapTagValueToString(newValue));
    });

    if (
        newValue.length === 0 ||
        newValue[0] === '' ||
        oldValue.toLowerCase() === mapTagValueToString(newValue).toLowerCase() ||
        newValueAlreadyExists
    ) {
        return state;
    }

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            tags: existingTags.map(savedTag =>
                mapTagValueToString(savedTag.value) === oldValue
                    ? mapTag(extendTagValue(newValue, savedTag.type, getInternalValueFromUserInput))
                    : savedTag
            )
        }
    };
};

const softDeleteTagInTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagInTagCloudTargetingSoftDeleteAction
): TagCloudTargetingsState => {
    const { editorId, tag } = action;
    const tagValue = mapTagValueToString(tag.value);
    const { tags } = state[editorId];
    const deletedTagIndex = tags.findIndex(it => mapTagValueToString(it.value) === tagValue);

    if (deletedTagIndex === -1) {
        return state;
    }

    const newTags = [...tags];
    newTags.splice(deletedTagIndex, 1);

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            tags: newTags
        }
    };
};

const deleteTagInTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagInTagCloudTargetingDeleteAction
): TagCloudTargetingsState => {
    const { editorId, tag } = action;
    const tagValue = mapTagValueToString(tag.value);
    const { tags, savedValue } = state[editorId];
    const deletedTagIndex = tags.findIndex(it => mapTagValueToString(it.value) === tagValue);
    const deletedTagIndexInSavedValue =
        savedValue && savedValue.tags.findIndex(it => mapTagValueToString(it.value) === tagValue);

    if (deletedTagIndex === -1) {
        return state;
    }

    const newTags = [...tags];
    newTags.splice(deletedTagIndex, 1);

    if (!savedValue || deletedTagIndexInSavedValue === -1) {
        return {
            ...state,
            [editorId]: {
                ...state[editorId],
                tags: newTags
            }
        };
    }

    const newSavedTags = [...savedValue.tags];
    newSavedTags.splice(deletedTagIndex, 1);

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            tags: newTags,
            savedValue: {
                tags: newSavedTags
            }
        }
    };
};

const loadTagCloudTargeting = (
    state: TagCloudTargetingsState,
    action: TagCloudTargetingLoadAction
): TagCloudTargetingsState => {
    const { editorId, shouldValueBeSaved, validateTag, getRenderValue } = action;

    if (!state[editorId]) {
        return state;
    }

    const savedValue = {
        tags: state[editorId].tags.map(tag => {
            if (tag.tagState !== TagState.LOADING) {
                return tag;
            }

            const renderValue = getRenderValue && getRenderValue(tag.value);

            if (validateTag) {
                return {
                    ...tag,
                    renderValue,
                    tagState: validateTag(tag.value) ? TagState.VALID : TagState.INVALID
                };
            }

            return {
                ...tag,
                renderValue,
                tagState: TagState.UNSET
            };
        })
    };

    if (shouldValueBeSaved) {
        return {
            ...state,
            [editorId]: {
                ...state[editorId],
                ...savedValue,
                savedValue
            }
        };
    }

    return {
        ...state,
        [editorId]: {
            ...state[editorId],
            ...savedValue
        }
    };
};

const updateTagCloudTargetings = (
    state: TagCloudTargetingsState,
    action: TagCloudTargetingsUpdateAction
): TagCloudTargetingsState => {
    return {
        ...state,
        ...action.editors
    };
};

export const tagCloudTargeting = (
    state: TagCloudTargetingsState = {},
    action: Action,
    fullState: State
): TagCloudTargetingsState => {
    switch (action.type) {
        case OPEN_TAG_CLOUD_TARGETING:
            return openTagCloudTargeting(state, action);
        case CLOSE_TAG_CLOUD_TARGETING:
            return closeTagCloudTargeting(state, action);
        case SAVE_TAG_CLOUD_TARGETING:
            return saveTagCloudTargeting(state, action);
        case ADD_TAGS_IN_TAG_CLOUD_TARGETING:
            return addTagsInTagCloudTargeting(state, action);
        case CHANGE_TAG_IN_TAG_CLOUD_TARGETING:
            return changeTagInTagCloudTargeting(state, action);
        case DELETE_TAG_IN_TAG_CLOUD_TARGETING:
            return deleteTagInTagCloudTargeting(state, action);
        case SOFT_DELETE_TAG_IN_TAG_CLOUD_TARGETING:
            return softDeleteTagInTagCloudTargeting(state, action);
        case UPDATE_TAG_CLOUD_TARGETINGS:
            return updateTagCloudTargetings(state, action);
        case LOAD_TAG_CLOUD_TARGETING:
            return loadTagCloudTargeting(state, action);
        default:
            return state;
    }
};
